import type {
  AISessionCreateMessageRequest,
  AISessionCreateMessageResponse,
} from '@/types/ai-session';
import { AI_SESSION_MACHINE_CODE_USER_MESSAGES } from '@/lib/ai-session/contracts/utils';

export type CreateMessageContractAction = 'reload' | 'none';

export const DEFAULT_CREATE_MESSAGE_INTERACTION_MODE = 'ask' as const;

export function parseCreateMessageResponse(value: unknown): AISessionCreateMessageResponse {
  if (!value || typeof value !== 'object') {
    throw new Error('AI_SESSION_CREATE_MESSAGE_RESPONSE_INVALID');
  }
  const response = value as Record<string, unknown>;
  const hasBaseFields =
    typeof response.messageId === 'string' &&
    typeof response.runId === 'string' &&
    response.runType === 'interaction';
  if (!hasBaseFields) {
    throw new Error('AI_SESSION_CREATE_MESSAGE_RESPONSE_INVALID');
  }
  if (
    response.admissionStatus === 'admitted' &&
    typeof response.streamUrl === 'string' &&
    response.streamUrl.length > 0 &&
    response.assistantMessageId === null &&
    response.blockedReason === null &&
    response.userMessage === null
  ) {
    return value as AISessionCreateMessageResponse;
  }
  if (
    response.admissionStatus === 'rejected' &&
    response.streamUrl === null &&
    typeof response.assistantMessageId === 'string' &&
    typeof response.blockedReason === 'string' &&
    typeof response.userMessage === 'string'
  ) {
    return value as AISessionCreateMessageResponse;
  }
  throw new Error('AI_SESSION_CREATE_MESSAGE_RESPONSE_INVALID');
}

export function shouldOpenCreateMessageStream(
  response: Pick<AISessionCreateMessageResponse, 'admissionStatus' | 'streamUrl'>
): response is AISessionCreateMessageResponse & { streamUrl: string } {
  if (response.admissionStatus === 'rejected') {
    return false;
  }
  const streamUrl = response.streamUrl;
  return typeof streamUrl === 'string' && streamUrl.length > 0;
}

export function shouldOpenCreateMessageStreamAfterError(_code?: string): boolean {
  return false;
}

export function resolveCreateMessageContractError(code: string | undefined): {
  action: CreateMessageContractAction;
} {
  if (code === 'CLIENT_VERSION_UNSUPPORTED') {
    return { action: 'reload' };
  }
  return { action: 'none' };
}

export function applyCreateMessageContractSideEffect(
  code: string | undefined,
  reload: () => void = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }
): void {
  if (resolveCreateMessageContractError(code).action === 'reload') {
    reload();
  }
}

export function buildCreateMessageRequestBody(
  request: AISessionCreateMessageRequest,
  generateIdempotencyKey: () => string
): {
  content: string;
  interactionMode: AISessionCreateMessageRequest['interactionMode'];
  readOnly?: true;
  consultIntent?: true;
  suppressDispatch?: true;
  sectionId?: string;
  sectionTitle?: string;
  executeAsEdit?: true;
  sourceMessageId?: string;
  alignmentReferences?: AISessionCreateMessageRequest['alignmentReferences'];
  breakdownReferences?: AISessionCreateMessageRequest['breakdownReferences'];
  clarificationAnswers?: AISessionCreateMessageRequest['clarificationAnswers'];
  modelName: string | null;
  responseMode: AISessionCreateMessageRequest['responseMode'] | null;
  requestIdempotencyKey: string;
} {
  const clarificationAnswers = request.clarificationAnswers;
  return {
    content: request.content,
    ...(request.alignmentReferences?.length
      ? { alignmentReferences: request.alignmentReferences }
      : {}),
    ...(request.breakdownReferences?.length
      ? { breakdownReferences: request.breakdownReferences }
      : {}),
    interactionMode: request.interactionMode,
    ...(request.readOnly ? { readOnly: true as const } : {}),
    ...(request.consultIntent ? { consultIntent: true as const } : {}),
    ...(request.suppressDispatch ? { suppressDispatch: true as const } : {}),
    ...(request.sectionId ? { sectionId: request.sectionId } : {}),
    ...(request.sectionTitle ? { sectionTitle: request.sectionTitle } : {}),
    ...(request.executeAsEdit ? { executeAsEdit: true as const } : {}),
    ...(request.executeAsEdit && request.sourceMessageId
      ? { sourceMessageId: request.sourceMessageId }
      : {}),
    // 空对象不发：服务端要求至少答一题，空 answers 会被 422。
    ...(clarificationAnswers && Object.keys(clarificationAnswers).length > 0
      ? { clarificationAnswers }
      : {}),
    modelName: request.modelName ?? null,
    responseMode: request.responseMode ?? null,
    requestIdempotencyKey: request.requestIdempotencyKey ?? generateIdempotencyKey(),
  };
}

export function resolveCreateMessageContractFeedback(code: string | undefined): {
  action: CreateMessageContractAction;
  retryable: boolean;
  ctaLabel: string | null;
  userMessage: string | null;
} {
  const { action } = resolveCreateMessageContractError(code);
  if (action === 'reload') {
    return {
      action,
      retryable: false,
      ctaLabel: null,
      userMessage: AI_SESSION_MACHINE_CODE_USER_MESSAGES.CLIENT_VERSION_UNSUPPORTED,
    };
  }
  return { action, retryable: true, ctaLabel: null, userMessage: null };
}

export function projectRejectedAdmission(
  response: Pick<
    AISessionCreateMessageResponse,
    'admissionStatus' | 'assistantMessageId' | 'userMessage' | 'streamUrl'
  >
): { openStream: false; assistantMessageId: string | null; userMessage: string } | null {
  if (response.admissionStatus !== 'rejected') {
    return null;
  }
  return {
    openStream: false,
    assistantMessageId: response.assistantMessageId ?? null,
    userMessage: response.userMessage ?? '',
  };
}

export const CREATE_MESSAGE_SAFE_REJECTION_USER_MESSAGE = '该请求无法处理，请修改后重试。';

export function rejectedAdmissionLeaksRaw(
  raw: string,
  response: Pick<
    AISessionCreateMessageResponse,
    'userMessage' | 'blockedReason' | 'assistantMessageId'
  >
): boolean {
  const needle = raw.trim();
  if (!needle) return false;
  return [response.userMessage, response.blockedReason, response.assistantMessageId].some(
    (item) => typeof item === 'string' && item.includes(needle)
  );
}

export function sanitizeRejectedAdmissionErrorMessage(
  rawUserContent: string,
  errorMessage: string
): string {
  return rejectedAdmissionLeaksRaw(rawUserContent, {
    userMessage: errorMessage,
    blockedReason: '',
    assistantMessageId: '',
  })
    ? CREATE_MESSAGE_SAFE_REJECTION_USER_MESSAGE
    : errorMessage;
}
